/**
 * Workspace-stream event catalog + subscriber registry (contract 05, ledger A17).
 *
 * The catalog owns event NAMES only. Payloads stay `unknown`: the feature that owns a domain
 * event also owns its frame parser, status machine and terminal semantics. The composition root
 * registers the names before the stream is installed; installation seals the catalog so a late
 * feature import cannot silently create an event that the already-open EventSource does not hear.
 */

export interface ShellEventMap {
  creative_labelling_progress: unknown
  creative_brief_progress: unknown
  causal_single_progress: unknown
  'custom_analysis.completed': unknown
  onboarding_task_update: unknown
  orpheus_chat_progress: unknown
  'stream.gap': unknown
}

export type ShellEventName = keyof ShellEventMap
export type ShellEventHandler<Name extends ShellEventName = ShellEventName> = (
  payload: ShellEventMap[Name],
) => void

/** Contract-owned workspace events; names only, never backend payload DTOs. */
export const SHELL_WORKSPACE_EVENTS = [
  'creative_labelling_progress',
  'creative_brief_progress',
  'causal_single_progress',
  'custom_analysis.completed',
  'onboarding_task_update',
  'orpheus_chat_progress',
  'stream.gap',
] as const satisfies readonly ShellEventName[]

const registeredEvents = new Set<ShellEventName>()
const handlers = new Map<ShellEventName, Set<ShellEventHandler>>()
const subscriberCountListeners = new Set<(count: number) => void>()
let sealed = false

function shellEventSubscriberCount(): number {
  let count = 0
  for (const eventHandlers of handlers.values()) count += eventHandlers.size
  return count
}

function notifySubscriberCountChanged(): void {
  const count = shellEventSubscriberCount()
  for (const listener of [...subscriberCountListeners]) listener(count)
}

/** Register transport event names during app composition. Re-registering a name is idempotent. */
export function registerShellEventNames(names: readonly ShellEventName[]): void {
  if (sealed) throw new Error('registerShellEventNames: registry is sealed')
  for (const name of names) registeredEvents.add(name)
}

/** Freeze the transport catalog and return its stable insertion order. */
export function sealShellEventNames(): readonly ShellEventName[] {
  sealed = true
  return [...registeredEvents]
}

/**
 * Subscribe to one registered event. The same handler is stored once per event; every unsubscribe
 * returned for a duplicate registration removes that canonical entry, closing the v3 D22 leak.
 */
export function onShellEvent<Name extends ShellEventName>(
  name: Name,
  handler: ShellEventHandler<Name>,
): () => void {
  if (!registeredEvents.has(name)) {
    throw new Error(`onShellEvent: "${name}" is not registered`)
  }
  const eventHandlers = handlers.get(name) ?? new Set<ShellEventHandler>()
  const wasAdded = !eventHandlers.has(handler as ShellEventHandler)
  eventHandlers.add(handler as ShellEventHandler)
  handlers.set(name, eventHandlers)
  if (wasAdded) notifySubscriberCountChanged()

  return () => {
    const wasDeleted = eventHandlers.delete(handler as ShellEventHandler)
    if (eventHandlers.size === 0) handlers.delete(name)
    if (wasDeleted) notifySubscriberCountChanged()
  }
}

/**
 * Transport lifecycle seam: the shell opens its workspace stream only while at least one
 * feature-owned handler is live. The registry reports unique handler leases, preserving the
 * duplicate-subscribe semantics above instead of counting wrapper calls.
 */
export function onShellEventSubscriberCountChange(listener: (count: number) => void): () => void {
  subscriberCountListeners.add(listener)
  return () => subscriberCountListeners.delete(listener)
}

export function getShellEventSubscriberCount(): number {
  return shellEventSubscriberCount()
}

/** Transport-only dispatch. Domain validation deliberately happens after this boundary. */
export function dispatchShellEvent<Name extends ShellEventName>(
  name: Name,
  payload: ShellEventMap[Name],
): void {
  for (const handler of [...(handlers.get(name) ?? [])]) {
    try {
      handler(payload)
    } catch {
      // One feature subscriber must not block the remaining handlers on the shared stream.
    }
  }
}

/** Logout/session reset: subscriptions are session-scoped and never survive tenant teardown. */
export function clearShellEventSubscribers(): void {
  const hadSubscribers = handlers.size > 0
  handlers.clear()
  if (hadSubscribers) notifySubscriberCountChanged()
}

/** Test isolation for the app-lifetime catalog. Production never unseals composition metadata. */
export function resetShellEventRegistryForTests(): void {
  clearShellEventSubscribers()
  subscriberCountListeners.clear()
  registeredEvents.clear()
  sealed = false
}
